/**
 * <LeadOutboundQueuePanel leadId={id} /> — Apex 2.0 Phase 3b.
 *
 * Shows the outbound-call queue for this lead + a "Re-engage with Apex"
 * button that schedules a new outbound call immediately. Apex will dial
 * the seller within ~60 seconds (next queue tick) and walk into the
 * conversation with prior call summaries already loaded as context.
 *
 * Endpoints:
 *   GET    /api/apex/outbound/queue?lead_id=... (filtered client-side)
 *   POST   /api/apex/outbound/queue
 *   DELETE /api/apex/outbound/queue/{id}
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Phone, PhoneOutgoing, X, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

const STATUS_STYLE = {
  queued:      { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   label: "Queued" },
  in_progress: { bg: "bg-indigo-50",  border: "border-indigo-200",  text: "text-indigo-700",  label: "Dialing" },
  completed:   { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Done" },
  failed:      { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700",    label: "Failed" },
  cancelled:   { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-500",   label: "Cancelled" },
};

export default function LeadOutboundQueuePanel({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/apex/outbound/queue?lead_id=${encodeURIComponent(leadId)}&limit=20`);
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const onSchedule = async () => {
    setScheduling(true);
    try {
      await api.post("/apex/outbound/queue", {
        lead_id: leadId, reason: "manual",
        note: "Re-engagement triggered from lead detail",
      });
      toast.success("Apex will call this seller within ~60 seconds");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule");
    } finally {
      setScheduling(false);
    }
  };

  const onCancel = async (id) => {
    if (!window.confirm("Cancel this queued outbound call?")) return;
    try {
      await api.delete(`/apex/outbound/queue/${id}`);
      toast.success("Outbound call cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <section className="card p-5" data-testid="lead-outbound-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            Apex 2.0 · Phase 3b
          </div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <PhoneOutgoing size={16} className="text-rose-600" /> Apex outbound
          </h3>
        </div>
        <button type="button" className="btn-money text-xs"
          onClick={onSchedule} disabled={scheduling}
          data-testid="lead-outbound-engage-btn">
          {scheduling ? <Loader2 size={12} className="animate-spin" /> : <Phone size={12} />}
          {scheduling ? "Queuing…" : "Re-engage now"}
        </button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-3">
          <Loader2 size={13} className="animate-spin inline mr-1.5" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-3" data-testid="lead-outbound-empty">
          No outbound calls queued. Click <strong>Re-engage now</strong> and Apex will call this seller within ~60 seconds with full context loaded.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="lead-outbound-list">
          {items.map((q) => {
            const style = STATUS_STYLE[q.status] || STATUS_STYLE.queued;
            return (
              <div key={q.id}
                className={`flex items-start gap-3 p-2.5 rounded-lg border ${style.border} ${style.bg}`}
                data-testid={`lead-outbound-row-${q.id}`}
              >
                <div className="shrink-0 w-7 h-7 rounded-lg bg-white flex items-center justify-center">
                  {q.status === "queued" ? <Clock size={13} className="text-amber-600" />
                    : q.status === "in_progress" ? <Loader2 size={13} className="animate-spin text-indigo-600" />
                    : q.status === "completed" ? <CheckCircle2 size={13} className="text-emerald-600" />
                    : q.status === "failed" ? <AlertCircle size={13} className="text-rose-600" />
                    : <X size={13} className="text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${style.text}`}>
                      {style.label}
                    </span>
                    <span className="font-normal text-[var(--ink-3)] text-[11px]">· {q.reason}</span>
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)]">
                    {q.scheduled_for ? `Scheduled ${fmt(q.scheduled_for)}` : ""}
                    {q.attempts > 0 ? ` · attempt ${q.attempts}/3` : ""}
                  </div>
                  {q.note && <div className="text-[10px] text-[var(--ink-3)] italic mt-0.5">{q.note}</div>}
                  {q.error && <div className="text-[10px] text-rose-700 mt-0.5">⚠ {q.error}</div>}
                </div>
                {q.status === "queued" && (
                  <button type="button"
                    className="btn-ghost text-xs text-rose-600 shrink-0"
                    title="Cancel"
                    onClick={() => onCancel(q.id)}
                    data-testid={`lead-outbound-cancel-${q.id}`}>
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
